import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  Injector,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormField, form, required, submit, validate } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import {
  folderOrDocumentNameErrorKey,
  validateFolderOrDocumentName,
} from '../services/folder-or-document-name';

interface NewFolderFormData {
  name: string;
}

@Component({
  selector: 'tf-new-folder-dialog',
  imports: [FormField, TranslateModule, ButtonModule, DialogModule, InputTextModule],
  templateUrl: './new-folder-dialog.component.html',
  styleUrl: './new-folder-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewFolderDialogComponent {
  private readonly injector = inject(Injector);

  readonly visible = input.required<boolean>();
  readonly isSaving = input(false);
  readonly errorMessage = input<string | null>(null);

  readonly save = output<string>();
  readonly cancelled = output<void>();
  readonly visibilityChange = output<boolean>();

  protected readonly formModel = signal<NewFolderFormData>({ name: '' });
  protected readonly newFolderForm = form(this.formModel, (schema) => {
    required(schema.name, { message: 'DOCUMENT_MANAGEMENT.NEW_FOLDER_NAME_REQUIRED' });
    validate(schema.name, ({ value }) => {
      const result = validateFolderOrDocumentName(value());
      return result.valid
        ? undefined
        : {
            kind: 'folder-or-document-name',
            message: folderOrDocumentNameErrorKey(result.reason),
          };
    });
  });

  /** Reference to the name input so we can focus it once the dialog has rendered. */
  protected readonly nameInput = viewChild<ElementRef<HTMLInputElement>>('nameInput');

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.formModel.set({ name: '' });
        // The dialog teleports its body to <body> via CDK overlay, so the input element exists
        // only after the next render tick. afterNextRender (within an injection context) ensures
        // we focus once the DOM is in place rather than fighting PrimeNG's own focus trap.
        afterNextRender(() => this.nameInput()?.nativeElement.focus(), { injector: this.injector });
      }
    });
  }

  protected onSave(): void {
    submit(this.newFolderForm, async () => {
      this.save.emit(validateFolderOrDocumentName(this.formModel().name).value);
    });
  }

  protected onCancel(): void {
    this.cancelled.emit();
  }

  protected onVisibilityChange(visible: boolean): void {
    this.visibilityChange.emit(visible);
  }
}
